/**
 * GitHub API service for installations and repo management.
 */

import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface Installation {
  installation_id: number;
  account_login: string;
  account_type: string;
  connected_at: string;
}

export interface Repo {
  owner: string;
  repo: string;
  full_name: string;
  description: string | null;
  default_branch: string;
  private: boolean;
  connected: boolean;
  /** Business-taxonomy tags ({dimKey: valueSlug}) from the repo inventory. */
  tags: Record<string, string>;
  /** Parsed .github/docs-standard.yml declaration (Documentation Standard
   *  §5.7), mirrored by the sync worker; absent when the repo has none. */
  docs_standard?: {
    standard?: string;
    level?: number;
    pillars?: Record<string, 'host' | 'consumer'>;
    aliases?: string[];
    adr_sequence?: string;
    /** Set when the committed file exists but could not be parsed. */
    invalid?: boolean;
  } | null;
}

export interface RepoConnection {
  owner: string;
  repo: string;
  full_name: string;
  sync_status: string;
  last_sync_at: string | null;
}

/** POST /github/repos/create — create a repo under an org installation. */
export interface CreateRepoRequest {
  owner: string;
  name: string;
  private?: boolean;
  description?: string;
}

/** Tenant plan + connected-repo usage (GET /github/repos/plan).
 *  `repo_limit: null` means unlimited (enterprise). */
export interface PlanUsage {
  plan: string;
  repo_limit: number | null;
  connected_count: number;
  at_limit: boolean;
}

/** Connect rejected with SYD-B001: the tenant is at its plan's
 *  connected-repo limit. Carries the body extras so the UI can render
 *  the limit modal without another round-trip. */
export class RepoLimitError extends Error {
  readonly limit: number;
  readonly connected: number;
  readonly plan: string;

  constructor(detail: string, extras: { limit: number; connected: number; plan: string }) {
    super(detail);
    this.name = 'RepoLimitError';
    this.limit = extras.limit;
    this.connected = extras.connected;
    this.plan = extras.plan;
  }
}

export const githubApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** A single-use state for an install link, issued to this user of this
   *  workspace (admin only). The callback refuses a redirect without it. */
  async issueInstallState(): Promise<{ state: string; expires_in: number }> {
    const { data } = await api.post('/github/auth/state');
    return data;
  },

  async oauthCallback(code: string, installationId: number, state: string): Promise<{ installation_id: number; account_login: string; message: string }> {
    const { data } = await api.post('/github/auth/callback', {
      code, installation_id: installationId, state,
    });
    return data;
  },

  async listInstallations(): Promise<Installation[]> {
    const { data } = await api.get('/github/auth/installations');
    return data;
  },

  async deleteInstallation(installationId: number): Promise<void> {
    await api.delete(`/github/auth/installations/${installationId}`);
  },

  async listRepos(): Promise<Repo[]> {
    const { data } = await api.get('/github/repos');
    return data;
  },

  async connectRepo(owner: string, repo: string): Promise<RepoConnection> {
    try {
      const { data } = await api.post(`/github/repos/${owner}/${repo}/connect`);
      return data;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.code === 'SYD-B001') {
        const body = e.response.data;
        throw new RepoLimitError(body.detail ?? 'Repository limit reached', {
          limit: body.limit ?? 1,
          connected: body.connected ?? 0,
          plan: body.plan ?? 'free',
        });
      }
      throw e;
    }
  },

  /** Create a repository under an organization installation (admin).
   *  App-created repos join the installation automatically, so the result
   *  can be appended to a listRepos() set without a refetch. */
  async createRepo(request: CreateRepoRequest): Promise<Repo> {
    const { data } = await api.post('/github/repos/create', request);
    return data;
  },

  async getPlanUsage(): Promise<PlanUsage> {
    const { data } = await api.get('/github/repos/plan');
    return data;
  },

  async disconnectRepo(owner: string, repo: string): Promise<void> {
    await api.delete(`/github/repos/${owner}/${repo}/disconnect`);
  },

  async getRepoStatus(owner: string, repo: string): Promise<RepoConnection> {
    const { data } = await api.get(`/github/repos/${owner}/${repo}/status`);
    return data;
  },

  /** Replace a repo's business-taxonomy tags (admin; full-map PUT semantics). */
  async setRepoTags(
    owner: string,
    repo: string,
    tags: Record<string, string>,
  ): Promise<{ repoFullName: string; tags: Record<string, string> }> {
    const { data } = await api.put(`/github/repos/${owner}/${repo}/tags`, { tags });
    return data;
  },

  async listWorkflowRuns(): Promise<{ id: number; name: string; status: string; conclusion: string | null; html_url: string; created_at: string; head_branch: string }[]> {
    const { data } = await api.get('/github/ci/runs');
    return data;
  },
};
